import { isAxiosError } from 'axios'
import { apiService } from '../../api-base'
import {
  AssignProductionRouteSheetPayload,
  BulkAssignProductionRouteSheetsData,
  BulkAssignProductionRouteSheetsPayload,
  CreateProductionEmployeePayload,
  CreateProductionMarkingBatchPayload,
  CreateProductionRouteSheetData,
  CreateProductionRouteSheetPayload,
  ProductionCatalogProduct,
  ProductionCatalogProductsParams,
  ProductionCatalogVariation,
  ProductionCatalogVariationsParams,
  ProductionAnalyticsData,
  ProductionAnalyticsParams,
  ProductionEmployeeArchiveBlockingRouteSheet,
  ProductionEmployeeArchiveConflictDetails,
  ProductionEmployeeData,
  ProductionEmployeesData,
  ProductionEmployeesParams,
  ProductionErrorInfo,
  ProductionErrorResponse,
  ProductionListParams,
  ProductionPrintDocument,
  ProductionMarkingBatch,
  ProductionMarkingBatchData,
  ProductionMarkingInventoryParams,
  ProductionMarkingInventoryProduct,
  ProductionMarkingImportError,
  ProductionPaginatedResponse,
  ProductionResponse,
  ProductionRouteSheetStatus,
  QualityControlProductionRouteSheetData,
  ProductionRouteSheet,
  ProductionRouteSheetData,
  ProductionRouteSheetScanContext,
  ProductionWorkloadData,
  ProductionWorkloadParams,
  QualityControlProductionRouteSheetPayload,
  UpdateProductionRouteSheetPayload,
  UpdateProductionEmployeePayload,
} from './types'

const PRODUCTION_URL = '/production'
const productionRouteSheetStatuses = new Set<ProductionRouteSheetStatus>([
  'created',
  'assigned',
  'quality_control',
  'quality_control_accepted',
  'completed',
  'received',
  'cancelled',
])

const productionErrorMessages: Record<string, string> = {
  employee_has_active_route_sheets:
    'Нельзя архивировать сотрудника. Сначала завершите или переназначьте активные маршрутные листы.',
  production_employee_not_found: 'Сотрудник не найден.',
  production_forbidden:
    'Недостаточно прав для выполнения этого действия. Обратитесь к администратору.',
  production_full_defect_forbidden:
    'Нельзя отметить браком всё количество изделий.',
  production_marking_batch_not_ready_for_archive:
    'Эту партию пока нельзя архивировать.',
  production_route_sheet_not_found: 'Маршрутный лист не найден.',
  production_route_sheet_not_ready_for_quality_control:
    'Маршрутный лист ещё не готов к контролю качества или уже обработан.',
  production_route_sheet_qr_not_available:
    'QR-код недоступен для ранее созданного маршрутного листа.',
  insufficient_marking_codes:
    'Недостаточно кодов маркировки для печати этикеток.',
  production_warehouse_not_configured:
    'Для юридического лица не настроен склад.',
  inventory_in_progress:
    'Невозможно принять продукцию на склад: по товару идет активная инвентаризация. Завершите инвентаризацию и повторите попытку.',
  validation_error: 'Проверьте правильность заполнения полей.',
}

const createProductionErrorInfo = (
  payload: ProductionErrorResponse | undefined,
  status: number | undefined,
  hasResponse: boolean,
): ProductionErrorInfo => {
  const code = payload?.status === false ? (payload.code ?? null) : null

  return {
    code,
    message:
      (code && productionErrorMessages[code]) ||
      (payload?.status === false ? payload.error : null) ||
      (hasResponse
        ? 'Не удалось выполнить запрос. Попробуйте ещё раз.'
        : 'Нет соединения с сервером. Проверьте интернет-соединение.'),
    status: status ?? null,
    details: payload?.status === false ? (payload.details ?? null) : null,
    fieldErrors: payload?.status === false ? (payload.errors ?? null) : null,
    isConflict: status === 409,
    isNotFound: status === 404,
  }
}

export function getProductionError(error: unknown): ProductionErrorInfo {
  if (!isAxiosError<ProductionErrorResponse>(error)) {
    return createProductionErrorInfo(undefined, undefined, true)
  }

  return createProductionErrorInfo(
    error.response?.data,
    error.response?.status,
    Boolean(error.response),
  )
}

const isEmployeeArchiveBlockingRouteSheet = (
  value: unknown,
): value is ProductionEmployeeArchiveBlockingRouteSheet => {
  if (typeof value !== 'object' || value === null) return false

  const routeSheet = value as Record<string, unknown>
  return (
    typeof routeSheet.id === 'string' &&
    typeof routeSheet.number === 'number' &&
    typeof routeSheet.status === 'string' &&
    productionRouteSheetStatuses.has(
      routeSheet.status as ProductionRouteSheetStatus,
    )
  )
}

export function getProductionEmployeeArchiveConflictDetails(
  error: ProductionErrorInfo,
): ProductionEmployeeArchiveConflictDetails | null {
  if (
    error.code !== 'employee_has_active_route_sheets' ||
    typeof error.details?.activeRouteSheetsCount !== 'number' ||
    !Array.isArray(error.details.activeRouteSheets) ||
    !error.details.activeRouteSheets.every(isEmployeeArchiveBlockingRouteSheet)
  ) {
    return null
  }

  return {
    activeRouteSheetsCount: error.details.activeRouteSheetsCount,
    activeRouteSheets: error.details.activeRouteSheets,
  }
}

export async function getProductionBlobError(
  error: unknown,
): Promise<ProductionErrorInfo> {
  if (!isAxiosError<Blob>(error) || !(error.response?.data instanceof Blob)) {
    return getProductionError(error)
  }

  try {
    const payload = JSON.parse(
      await error.response.data.text(),
    ) as ProductionErrorResponse

    return createProductionErrorInfo(payload, error.response.status, true)
  } catch {
    return createProductionErrorInfo(
      undefined,
      error.response.status,
      Boolean(error.response),
    )
  }
}

export class ProductionService {
  static getProductAnalytics(
    productAvailabilityId: string,
    params: ProductionAnalyticsParams = {},
  ): Promise<ProductionResponse<ProductionAnalyticsData>> {
    return apiService.get(
      `${PRODUCTION_URL}/analytics/products/${productAvailabilityId}`,
      { params },
    )
  }

  static getCatalogProducts(
    params: ProductionCatalogProductsParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionCatalogProduct>> {
    return apiService.get(`${PRODUCTION_URL}/catalog/products`, { params })
  }

  static getCatalogVariations(
    params: ProductionCatalogVariationsParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionCatalogVariation>> {
    return apiService.get(`${PRODUCTION_URL}/catalog/variations`, { params })
  }

  static getRouteSheets(
    params: ProductionListParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionRouteSheet>> {
    return apiService.get(`${PRODUCTION_URL}/route-sheets`, { params })
  }

  static getRouteSheet(
    routeSheetId: string,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.get(`${PRODUCTION_URL}/route-sheets/${routeSheetId}`)
  }

  static resolveRouteSheetQr(
    token: string,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.post(`${PRODUCTION_URL}/route-sheets/resolve-qr`, {
      token,
    })
  }

  static resolveRouteSheetScan(
    code: string,
    context: ProductionRouteSheetScanContext,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.post(`${PRODUCTION_URL}/route-sheets/resolve-scan`, {
      code,
      context,
    })
  }

  static getRouteSheetPdf(
    routeSheetId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return apiService.get(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}/print`,
      {
        responseType: 'blob',
        signal,
      },
    )
  }

  static getPrintDocument(
    url: ProductionPrintDocument['url'],
    signal?: AbortSignal,
  ): Promise<Blob> {
    return apiService.get(url, { responseType: 'blob', signal })
  }

  static createRouteSheet(
    payload: CreateProductionRouteSheetPayload,
  ): Promise<ProductionResponse<CreateProductionRouteSheetData>> {
    return apiService.post(`${PRODUCTION_URL}/route-sheets`, payload)
  }

  static updateRouteSheet(
    routeSheetId: string,
    payload: UpdateProductionRouteSheetPayload,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.patch(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}`,
      payload,
    )
  }

  static deleteRouteSheet(routeSheetId: string): Promise<void> {
    return apiService.delete<void>(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}`,
    )
  }

  static assignRouteSheet(
    routeSheetId: string,
    payload: AssignProductionRouteSheetPayload,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.post(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}/assign`,
      payload,
    )
  }

  static bulkAssignRouteSheets(
    payload: BulkAssignProductionRouteSheetsPayload,
  ): Promise<ProductionResponse<BulkAssignProductionRouteSheetsData>> {
    return apiService.post(
      `${PRODUCTION_URL}/route-sheets/bulk-assign`,
      payload,
    )
  }

  static printRouteSheetsBatch(routeSheetIds: string[]): Promise<Blob> {
    return apiService.post(
      `${PRODUCTION_URL}/route-sheets/print-batch`,
      { routeSheetIds },
      { responseType: 'blob' },
    )
  }

  static sendRouteSheetToQualityControl(
    routeSheetId: string,
  ): Promise<ProductionResponse<ProductionRouteSheetData>> {
    return apiService.post(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}/send-to-quality-control`,
    )
  }

  static qualityControlRouteSheet(
    routeSheetId: string,
    payload: QualityControlProductionRouteSheetPayload,
  ): Promise<ProductionResponse<QualityControlProductionRouteSheetData>> {
    return apiService.post(
      `${PRODUCTION_URL}/route-sheets/${routeSheetId}/quality-control`,
      payload,
    )
  }

  static getEmployees(
    params: ProductionEmployeesParams = {},
  ): Promise<ProductionResponse<ProductionEmployeesData>> {
    return apiService.get(`${PRODUCTION_URL}/employees`, { params })
  }

  static getEmployeePdf(employeeId: string): Promise<Blob> {
    return apiService.get(`${PRODUCTION_URL}/employees/${employeeId}/print`, {
      responseType: 'blob',
    })
  }

  static resolveEmployeeQr(
    qrToken: string,
  ): Promise<ProductionResponse<ProductionEmployeeData>> {
    return apiService.post(`${PRODUCTION_URL}/employees/resolve-qr`, {
      qrToken,
    })
  }

  static createEmployee(
    payload: CreateProductionEmployeePayload,
  ): Promise<ProductionResponse<ProductionEmployeeData>> {
    return apiService.post(`${PRODUCTION_URL}/employees`, payload)
  }

  static updateEmployee(
    employeeId: string,
    payload: UpdateProductionEmployeePayload,
  ): Promise<ProductionResponse<ProductionEmployeeData>> {
    return apiService.patch(
      `${PRODUCTION_URL}/employees/${employeeId}`,
      payload,
    )
  }

  static archiveEmployee(
    employeeId: string,
  ): Promise<ProductionResponse<ProductionEmployeeData>> {
    return apiService.post(`${PRODUCTION_URL}/employees/${employeeId}/archive`)
  }

  static restoreEmployee(
    employeeId: string,
  ): Promise<ProductionResponse<ProductionEmployeeData>> {
    return apiService.post(`${PRODUCTION_URL}/employees/${employeeId}/restore`)
  }

  static getEmployeeWorkload(
    params: ProductionWorkloadParams = {},
  ): Promise<ProductionResponse<ProductionWorkloadData>> {
    return apiService.get(`${PRODUCTION_URL}/employees/workload`, { params })
  }

  static getMarkingBatches(
    params: ProductionListParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionMarkingBatch>> {
    return apiService.get(`${PRODUCTION_URL}/marking/batches`, { params })
  }

  static getMarkingInventory(
    params: ProductionMarkingInventoryParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionMarkingInventoryProduct>> {
    return apiService.get(`${PRODUCTION_URL}/marking/inventory`, { params })
  }

  static createMarkingBatch({
    productVariationId,
    file,
  }: CreateProductionMarkingBatchPayload): Promise<
    ProductionResponse<ProductionMarkingBatchData>
  > {
    const data = new FormData()
    data.append('productVariationId', productVariationId)
    data.append('file', file)

    return apiService.post(`${PRODUCTION_URL}/marking/batches`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  static getMarkingBatchErrors(
    batchId: string,
    params: ProductionListParams = {},
  ): Promise<ProductionPaginatedResponse<ProductionMarkingImportError>> {
    return apiService.get(
      `${PRODUCTION_URL}/marking/batches/${batchId}/errors`,
      { params },
    )
  }

  static archiveMarkingBatch(
    batchId: string,
  ): Promise<ProductionResponse<ProductionMarkingBatchData>> {
    return apiService.post(
      `${PRODUCTION_URL}/marking/batches/${batchId}/archive`,
    )
  }
}

export * from './types'
